import {
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import { useProvider } from '@newfront-insurance/react-provision';
import { getConfig } from '@newfront-insurance/shared-public-config';
import { ExternalLink, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { BrowserLiveView } from './browser-live-view-lazy';
import { useNovaContext } from '../../../providers';

// Kept intentionally lightweight vs the artifact preview panel — a browser
// session isn't a downloadable file. This module only needs: (1) open with a
// live-view URL and title, (2) render the DCV live stream (or a screenshot
// fallback) in a focused modal, (3) close on X or Esc.

export interface BrowserPreviewPayload {
  /** Signed live-view URL — AgentCore returns a SigV4-presigned DCV endpoint. */
  liveViewUrl?: string;
  /** How to render `liveViewUrl`. `'dcv'` decodes via the DCV Web Client SDK. */
  viewerType?: 'dcv';
  /** Screenshot to fall back to when no live view (e.g. session already closed). */
  screenshotUrl?: string;
  /** Header title. Usually the browserUse task or the current step summary. */
  title: string;
  /** Optional short subtitle (current URL, action summary). */
  subtitle?: string;
  /** Opaque server-issued handoff handle used to obtain a current signed live-view URL. */
  handoffId?: string;
}

interface BrowserPreviewContextValue {
  open: (payload: BrowserPreviewPayload) => void;
  close: () => void;
  isOpen: boolean;
}

const BrowserPreviewContext = createContext<BrowserPreviewContextValue | null>(null);

function getBrowserHandoffNewTabHref(handoffId: string): string {
  const pathname = typeof window === 'undefined' ? '/' : window.location.pathname;
  const mountPath = pathname === '/chat' || pathname.startsWith('/chat/') ? '/chat' : '';
  return `${mountPath}/browser-handoffs/${encodeURIComponent(handoffId)}`;
}

/** Optional accessor — returns null when the provider isn't mounted (e.g. in message renderers used outside the chat shell). */
export function useBrowserPreviewSafe(): BrowserPreviewContextValue | null {
  return useContext(BrowserPreviewContext);
}

export function BrowserPreviewProvider({ children }: { children: ReactNode }) {
  const { authProvider, baseUrl } = useNovaContext();
  const { getToken } = useProvider(authProvider);
  const [payload, setPayload] = useState<BrowserPreviewPayload | null>(null);
  const [refreshError, setRefreshError] = useState<string>();
  const transferredHandoffIdsRef = useRef(new Set<string>());

  const open = useCallback(
    (next: BrowserPreviewPayload) => {
      const { handoffId } = next;
      setRefreshError(undefined);
      if (!handoffId) {
        setPayload(next);
        return;
      }

      const mustRefreshLiveView = transferredHandoffIdsRef.current.delete(handoffId);
      // The active browser step already contains a valid live-view URL. Keep
      // it mounted immediately; only call the refresh endpoint when a caller
      // has no current URL to display, or when the stream was transferred to
      // and released by a separate browser tab.
      const nextPayload = mustRefreshLiveView ? { ...next, liveViewUrl: undefined } : next;
      setPayload(nextPayload);
      if (nextPayload.liveViewUrl) return;

      void (async () => {
        try {
          const token = await getToken();
          const apiUrl = `${(baseUrl ?? getConfig().API.AI).replace(/\/$/, '')}/api/ai/nova/v2/browser-handoffs/${handoffId}/live-view`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
          });
          if (!response.ok) throw new Error(`Unable to refresh browser session (${response.status})`);
          const body = (await response.json()) as { liveViewUrl?: unknown };
          if (typeof body.liveViewUrl !== 'string') throw new Error('Unable to refresh browser session');
          const liveViewUrl = body.liveViewUrl;
          setPayload((current) =>
            current?.handoffId === handoffId ? { ...nextPayload, handoffId, liveViewUrl } : current,
          );
        } catch (error) {
          setRefreshError(error instanceof Error ? error.message : 'Unable to refresh browser session');
        }
      })();
    },
    [baseUrl, getToken],
  );

  const close = useCallback(() => {
    setPayload(null);
  }, []);

  const openInNewTab = useCallback(
    (handoffId: string) => {
      // AgentCore permits only one Live View stream per browser session. If
      // the user returns here after closing the external viewer, reacquire a
      // fresh stream URL instead of reusing this viewer's former URL.
      transferredHandoffIdsRef.current.add(handoffId);
      close();
    },
    [close],
  );

  useEffect(() => {
    if (!payload) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true });
  }, [payload, close]);

  const value = useMemo<BrowserPreviewContextValue>(
    () => ({ open, close, isOpen: payload !== null }),
    [open, close, payload],
  );
  const handoffId = payload?.handoffId;
  let newTabHref: string | undefined;
  if (handoffId) {
    newTabHref = getBrowserHandoffNewTabHref(handoffId);
  } else if (payload?.viewerType !== 'dcv') {
    newTabHref = payload?.liveViewUrl;
  }

  return (
    <BrowserPreviewContext.Provider value={value}>
      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
        {payload ? (
          <TooltipProvider delayDuration={0}>
            <Dialog open onOpenChange={(isOpen) => !isOpen && close()}>
              <DialogContent
                showCloseButton={false}
                className={cn(
                  'border-steel-75 flex h-[calc(100vh-2rem)] max-h-[900px] w-[calc(100vw-2rem)] max-w-[1440px] flex-col gap-0 overflow-hidden rounded-lg bg-white p-0 font-sans',
                )}
              >
                <DialogHeader className="border-steel-75 flex h-14 shrink-0 flex-row items-center justify-between gap-2 border-b bg-white px-4 py-0 text-left">
                  <div className="flex min-w-0 flex-col">
                    <DialogTitle className="text-steel-500 truncate text-xs font-semibold">{payload.title}</DialogTitle>
                    {payload.subtitle ? (
                      <div className="text-steel-400 truncate text-[11px]">{payload.subtitle}</div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {newTabHref ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={newTabHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open live view in a new tab"
                            className="text-steel-400 hover:bg-steel-100 hover:text-steel-500 flex size-6 items-center justify-center rounded-sm transition-colors"
                            onClick={handoffId ? () => openInNewTab(handoffId) : undefined}
                          >
                            <ExternalLink className="size-4" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>Open in new tab</TooltipContent>
                      </Tooltip>
                    ) : null}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Close browser preview"
                          className="text-steel-400 hover:bg-steel-100 hover:text-steel-500 flex size-6 items-center justify-center rounded-sm transition-colors"
                          onClick={close}
                        >
                          <X className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Close</TooltipContent>
                    </Tooltip>
                  </div>
                </DialogHeader>
                <div className="bg-steel-50 min-h-0 flex-1 p-3">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {payload.liveViewUrl && payload.viewerType === 'dcv' ? (
                    <div className="border-steel-75 relative h-full w-full overflow-hidden rounded border bg-white">
                      <BrowserLiveView key={payload.liveViewUrl} signedUrl={payload.liveViewUrl} />
                    </div>
                  ) : payload.screenshotUrl ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={payload.screenshotUrl}
                      alt={payload.title}
                      className="border-steel-75 h-full w-full rounded border bg-white object-contain"
                    />
                  ) : refreshError ? (
                    <div className="text-steel-400 flex h-full items-center justify-center text-xs">{refreshError}</div>
                  ) : (
                    <div className="text-steel-400 flex h-full items-center justify-center text-xs">
                      Connecting to secure browser…
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </TooltipProvider>
        ) : null}
      </div>
    </BrowserPreviewContext.Provider>
  );
}
